/**
 * Подгрузка справочников порциями (AK-574).
 *
 * До этого каждый reference-экран запрашивал одно фиксированное окно
 * (`page_size=100` у культур, `limit=200` у болезней/вредителей/сорняков и
 * моделей техники) и рисовал рядом честный серверный `total`. При 340
 * найденных пользователь видел «найдено 340» и 200 строк — без индикации,
 * что список оборван, и без способа добрать остальное. Поиск при этом
 * серверный, так что «не вижу — поищу» работало, а «пролистаю до конца» —
 * нет.
 *
 * Курсор — `offset`, как в `usePlatformCompaniesInfinite` (AK-348): следующая
 * порция запрашивается, пока накопленных строк меньше `total`. Ручки
 * `limit/offset` + `total` на бэкенде уже были у всех пяти каталогов —
 * добавлять на сервере ничего не пришлось.
 */
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';

/** Порция. Одинаковая для всех справочников — намеренно, чтобы поведение не «плавало». */
export const REFERENCE_PAGE_SIZE = 100;

export interface PagedInfiniteResult<T> {
  items: T[];
  total: number;
}

interface PagedPayload<T> {
  items: T[];
  total: number;
}

export function usePagedInfinite<T>(
  queryKey: readonly unknown[],
  buildUrl: (offset: number, limit: number) => string,
  options?: { enabled?: boolean; pageSize?: number; staleTime?: number },
): UseInfiniteQueryResult<PagedInfiniteResult<T>, Error> {
  const pageSize = options?.pageSize ?? REFERENCE_PAGE_SIZE;
  return useInfiniteQuery({
    queryKey,
    initialPageParam: 0,
    enabled: options?.enabled ?? true,
    staleTime: options?.staleTime ?? 30_000,
    queryFn: ({ pageParam }) =>
      api.get<PagedPayload<T>>(buildUrl(pageParam as number, pageSize)),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      // Пустая порция при недосчитанном total — стоп, иначе бесконечный цикл.
      if (lastPage.items.length === 0) return undefined;
      return loaded < lastPage.total ? loaded : undefined;
    },
    select: (data) => ({
      items: data.pages.flatMap((p) => p.items),
      total: data.pages[0]?.total ?? 0,
    }),
  });
}
